import { useMemo } from 'react';
import { useDrawHistory } from '@/hooks/useDrawHistory';
import {
  applyAdvancedFilters,
  computeFrequency,
  computeStatsSummary,
  filterByPeriod,
  getColdCombination,
  getColdNumbers,
  getHotCombination,
  getHotNumbers,
  type AdvancedFilters,
  type StatsSummary,
} from '@/lib/statisticsAnalysis';

// 이전 API 호환을 유지하는 얇은 래퍼. 원본 로드는 useDrawHistory가 담당하고
// 기간/고급 필터·파생 통계는 statisticsAnalysis 순수 함수를 재사용한다.
// (SmartRecommender 등 홈 화면에서 사용)

export type { Draw } from '@/lib/drawHistory';
export type { StatsSummary, AdvancedFilters };

export function useLottoData(limit?: number, advancedFilters?: AdvancedFilters) {
  const {
    draws: allDraws,
    loading,
    error,
    dbCount,
    loadedCount,
    invalidCount,
    incomplete,
    incompleteReason,
    retry,
  } = useDrawHistory();

  const periodFilteredDraws = useMemo(
    () => filterByPeriod(allDraws, limit),
    [allDraws, limit],
  );

  const filteredDraws = useMemo(
    () => applyAdvancedFilters(periodFilteredDraws, advancedFilters),
    [periodFilteredDraws, advancedFilters],
  );

  const frequencyData = useMemo(
    () => computeFrequency(filteredDraws),
    [filteredDraws],
  );

  const hotNumbers = useMemo(() => getHotNumbers(frequencyData), [frequencyData]);
  const coldNumbers = useMemo(() => getColdNumbers(frequencyData), [frequencyData]);
  const hotCombination = useMemo(() => getHotCombination(frequencyData), [frequencyData]);
  const coldCombination = useMemo(() => getColdCombination(frequencyData), [frequencyData]);

  const statsSummary = useMemo(
    () => computeStatsSummary(filteredDraws, frequencyData, hotNumbers, coldNumbers),
    [filteredDraws, frequencyData, hotNumbers, coldNumbers],
  );

  return {
    draws: filteredDraws,
    allDraws,
    periodFilteredDraws,
    loading,
    error,
    dbCount,
    loadedCount,
    invalidCount,
    incomplete,
    incompleteReason,
    retry,
    frequencyData,
    hotNumbers,
    coldNumbers,
    hotCombination,
    coldCombination,
    statsSummary,
  };
}